import React, { useEffect, useRef, useState } from 'react';
import { X, Hash, AlertCircle, Loader2 } from 'lucide-react';
import { useTranslation } from '../../../../contexts/I18nProvider';
import { itemLocationBalanceService } from '../../../../services/itemLocationBalanceService';
import { ItemLocationBalanceDto } from '../../../../types/itemLocationBalance';
import { ScanTextInput } from '../../../common/inputs';

interface CorrectSerialModalProps {
  /** Raden som skal rettes — modal vises kun når denne er satt */
  row: ItemLocationBalanceDto;
  onClose: () => void;
  /** Kalles etter vellykket retting, med ferdig formatert melding til brukeren */
  onSuccess: (message: string) => void;
}

/**
 * Rett et feilregistrert serienummer (ItemType 6) direkte fra Varelokasjonssaldo.
 * Ren rename i ItemBins — saldo og lokasjon røres ikke; backend skriver
 * SERIEFIX-korreksjonspar i transaksjonshistorikken. Kun lagersjef/administrator.
 */
export const CorrectSerialModal: React.FC<CorrectSerialModalProps> = ({ row, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [newSerialNo, setNewSerialNo] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const trimmedNew = newSerialNo.trim();
  const canSave = trimmedNew.length > 0
    && trimmedNew.toLowerCase() !== (row.serialNo || '').trim().toLowerCase()
    && !saving;

  const handleSave = async () => {
    if (!canSave || !row.serialNo) return;
    try {
      setSaving(true);
      setError(null);
      const result = await itemLocationBalanceService.correctSerialNo(row.id, row.serialNo, trimmedNew);
      onSuccess(
        t('manager.modules.itemLocationBalance.serialCorrection.success',
          { '0': result.oldSerialNo, '1': result.newSerialNo, '2': result.itemNo },
          `Serienummer endret fra ${result.oldSerialNo} til ${result.newSerialNo} for vare ${result.itemNo}.`)
      );
    } catch (err) {
      setError(err instanceof Error
        ? err.message
        : t('manager.modules.itemLocationBalance.serialCorrection.errorGeneric', {}, 'Kunne ikke rette serienummer'));
      setSaving(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && canSave) {
      e.preventDefault();
      handleSave();
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Hash className="text-blue-600" size={22} />
            <h2 className="text-lg font-bold text-gray-900">
              {t('manager.modules.itemLocationBalance.serialCorrection.title', {}, 'Rett serienummer')}
            </h2>
          </div>
          <button
            onClick={onClose}
            disabled={saving}
            className="text-gray-400 hover:text-gray-600 transition-colors disabled:opacity-50"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-4 space-y-4">
          <div className="text-sm text-gray-700 space-y-1">
            <div>
              <span className="text-gray-500">{t('manager.modules.itemLocationBalance.serialCorrection.item', {}, 'Vare')}: </span>
              <span className="font-medium">{row.itemNo}</span>
              {row.itemDescription && <span className="text-gray-500"> — {row.itemDescription}</span>}
            </div>
            <div>
              <span className="text-gray-500">{t('manager.modules.itemLocationBalance.serialCorrection.location', {}, 'Lokasjon')}: </span>
              <span className="font-mono font-medium">{row.binCode}</span>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('manager.modules.itemLocationBalance.serialCorrection.oldSerial', {}, 'Gammelt serienummer')}
            </label>
            <input
              type="text"
              value={row.serialNo || ''}
              disabled
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm font-mono bg-gray-100 text-gray-600"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {t('manager.modules.itemLocationBalance.serialCorrection.newSerial', {}, 'Nytt serienummer')}
            </label>
            <ScanTextInput
              ref={inputRef}
              value={newSerialNo}
              onChange={(e) => setNewSerialNo(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder={t('manager.modules.itemLocationBalance.serialCorrection.newSerialPlaceholder', {}, 'Skann eller skriv nytt serienummer...')}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>

          <p className="text-xs text-gray-500">
            {t('manager.modules.itemLocationBalance.serialCorrection.note', {},
              'Saldo og lokasjon endres ikke. Rettingen dokumenteres i varehistorikken med årsak SERIEFIX.')}
          </p>

          {error && (
            <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              <AlertCircle size={16} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t flex justify-end gap-3">
          <button
            onClick={onClose}
            disabled={saving}
            className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
          >
            {t('common.buttons.cancel', {}, 'Avbryt')}
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave}
            className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving && <Loader2 size={14} className="animate-spin" />}
            {t('manager.modules.itemLocationBalance.serialCorrection.confirm', {}, 'Rett serienummer')}
          </button>
        </div>
      </div>
    </div>
  );
};
